<template>
    <div class="users-page">
        <header class="users-head">
            <span class="head-icon"><img src="images/icons/user-circle.svg" alt="" /></span>
            <div>
                <h1>{{ t('users-title') }}</h1>
                <p>{{ t('users-subtitle') }}</p>
            </div>
        </header>
        <div class="box">
            <div class="users-toolbar">
                <div class="toolbar-main">
                    <label class="search">
                        <img src="images/icons/search-md.svg" alt="" />
                        <input v-model="q" :placeholder="t('users-search')" @input="scheduleLoad" />
                    </label>
                    <button type="button" class="filter-btn" :class="{ on: filtersOpen }" @click="filtersOpen = !filtersOpen" :aria-label="t('users-filters-products')">
                        <img src="images/icons/filter-lines.svg" alt="" />
                    </button>
                    <router-link class="add" to="/settings/users/new">
                        <img src="images/icons/plus-white.svg" alt="" />
                        {{ t('users-add') }}
                    </router-link>
                </div>
                <transition name="filters">
                    <div v-if="filtersOpen" key="filters" class="filter-row">
                        <div v-for="group in filterGroups" :key="group.id" class="pill" :class="{ open: openFilter === group.id }">
                            <button type="button" @click.stop="toggleFilter(group.id, $event)">
                                <span>{{ t(group.label) }}</span>
                                <img src="images/icons/chevron-down.svg" alt="" />
                            </button>
                            <transition name="pop">
                                <ul v-if="openFilter === group.id" class="filter-menu" :style="filterStyle" @click.stop>
                                    <li v-for="option in group.options" :key="option.value">
                                        <button type="button" @click="pickFilter(group.id, option.value)">
                                            <i class="tick" :class="{ on: isPicked(group.id, option.value) }"></i>
                                            {{ t(option.label) }}
                                        </button>
                                    </li>
                                </ul>
                            </transition>
                        </div>
                    </div>
                </transition>
            </div>

            <div v-if="loading && !items.length" class="skeleton" role="status" :aria-label="t('users-loading')">
                <div v-for="row in 6" :key="'sk-' + row" class="skeleton-row">
                    <span class="sk sk-avatar"></span>
                    <span class="sk sk-name"></span>
                    <span class="sk sk-wide"></span>
                    <span class="sk sk-mid"></span>
                    <span class="sk sk-short"></span>
                </div>
            </div>
            <div v-else-if="error" class="empty">
                <span class="empty-icon alert"><img src="images/icons/alert-triangle.svg" alt="" /></span>
                <h2>{{ t('users-load_error') }}</h2>
                <p>{{ t('users-load_error_hint') }}</p>
                <div class="empty-actions">
                    <button type="button" class="add" @click="load">{{ t('users-reload') }}</button>
                </div>
            </div>
            <div v-else-if="!items.length" class="empty">
                <span class="empty-icon"><img src="images/icons/user-circle.svg" alt="" /></span>
                <h2>{{ t('users-empty') }}</h2>
                <p>{{ hasFilters ? t('users-empty_filters') : t('users-empty_hint') }}</p>
                <div class="empty-actions">
                    <button v-if="hasFilters" type="button" class="ghost" @click="clearFilters">{{ t('users-clear_filters') }}</button>
                    <router-link class="add" to="/settings/users/new">
                        <img src="images/icons/plus-white.svg" alt="" />
                        {{ t('users-add') }}
                    </router-link>
                </div>
            </div>

            <table v-else-if="!isCompact" class="users-table">
                <colgroup>
                    <col class="col-user" />
                    <col class="col-email" />
                    <col class="col-phone" />
                    <col class="col-twofa" />
                    <col class="col-access" />
                    <col class="col-menu" />
                </colgroup>
                <thead>
                    <tr>
                        <th v-for="column in sortColumns" :key="column.key" :aria-sort="ariaSort(column.key)">
                            <button type="button" class="sort-btn" :class="{ on: sort === column.key }" @click="sortBy(column.key)">
                                {{ t(column.label) }}
                                <img src="images/icons/chevron-down.svg" alt="" :class="{ up: sort === column.key && dir === 'asc', idle: sort !== column.key }" />
                            </button>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in items" :key="user.id">
                        <td>
                            <div class="person">
                                <img v-if="user.logo" :src="user.logo" class="avatar" alt="" />
                                <span v-else class="avatar">{{ initials(user.name) }}</span>
                                <div class="who">
                                    <kv-tooltip :text="user.name"><strong>{{ user.name }}</strong></kv-tooltip>
                                    <kv-tooltip :text="'@' + user.username"><span>@{{ user.username }}</span></kv-tooltip>
                                </div>
                                <span v-if="user.active === false" class="tag tag-off">Inativo</span>
                                <span v-if="user.is_internal" class="tag tag-internal">Interno</span>
                                <button v-if="user.active === false && isGlobalAdmin" type="button" class="tag-action" @click.stop="askActivate(user)">Ativar</button>
                                <span v-else-if="!user.in_kevi_chat && !user.in_kevi_marketing" class="tag">Sem acesso a apps</span>
                                <span v-if="user.show_product_icons" class="products">
                                    <kv-tooltip v-if="user.in_kevi_chat" :text="t('users-tooltip_chat')"><span class="product"><img src="images/icons/logo-chat.svg" alt="" /></span></kv-tooltip>
                                    <kv-tooltip v-if="user.in_kevi_marketing" :text="t('users-tooltip_mkt')"><span class="product"><img src="images/icons/logo-mkt.svg" alt="" /></span></kv-tooltip>
                                </span>
                            </div>
                        </td>
                        <td>
                            <kv-tooltip v-if="!user.email && !user.phone" :text="t('users-tooltip_no_contact')"><span class="warn-contact">{{ t('users-no_contact') }}</span></kv-tooltip>
                            <span v-else-if="user.email" class="contact-line contact-email">
                                <img src="images/icons/mail.svg" alt="" />
                                <span class="contact-text">{{ user.email }}</span>
                                <kv-tooltip v-if="!user.email_verified" :text="t('users-tooltip_email_pending')"><em class="pending"><img src="images/icons/alert-circle.svg" alt="" /></em></kv-tooltip>
                            </span>
                            <span v-else class="muted">{{ t('users-no_email') }}</span>
                        </td>
                        <td>
                            <span v-if="user.phone" class="contact-line">
                                <img src="images/icons/phone.svg" alt="" />
                                {{ user.phone }}
                                <kv-tooltip v-if="!user.phone_verified" :text="t('users-tooltip_phone_pending')"><em class="pending"><img src="images/icons/alert-circle.svg" alt="" /></em></kv-tooltip>
                            </span>
                            <span v-else class="muted">{{ t('users-no_phone') }}</span>
                        </td>
                        <td>
                            <span class="contact-line label" :class="{ 'is-twofa-on': user.twofa === 'on' }">
                                <img :src="user.twofa === 'on' ? 'images/icons/shield-tick-green.svg' : 'images/icons/shield-off.svg'" alt="" />
                                {{ user.twofa === 'on' ? t('users-twofa_on') : t('users-twofa_off') }}
                            </span>
                        </td>
                        <td class="when label">{{ formatAccess(user.last_access) }}</td>
                        <td class="menu-cell">
                            <button type="button" class="kebab" @click.stop="openMenu(user, $event)" aria-label="Ações"><img src="images/icons/dots.svg" alt="" /></button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-else class="user-cards">
                <article v-for="user in items" :key="'card-' + user.id" class="user-card">
                    <div class="card-top">
                        <img v-if="user.logo" :src="user.logo" class="avatar" alt="" />
                        <span v-else class="avatar">{{ initials(user.name) }}</span>
                        <div class="who">
                            <strong>{{ user.name }}</strong>
                            <span>@{{ user.username }}</span>
                        </div>
                        <span v-if="user.active === false" class="tag tag-off">Inativo</span>
                                <span v-if="user.is_internal" class="tag tag-internal">Interno</span>
                        <button v-if="user.active === false && isGlobalAdmin" type="button" class="tag-action" @click.stop="askActivate(user)">Ativar</button>
                        <span v-else-if="!user.in_kevi_chat && !user.in_kevi_marketing" class="tag">Sem acesso a apps</span>
                        <kv-tooltip v-if="user.show_product_icons && user.in_kevi_chat" :text="t('users-tooltip_chat')"><span class="product"><img src="images/icons/logo-chat.svg" alt="" /></span></kv-tooltip>
                        <kv-tooltip v-if="user.show_product_icons && user.in_kevi_marketing" :text="t('users-tooltip_mkt')"><span class="product"><img src="images/icons/logo-mkt.svg" alt="" /></span></kv-tooltip>
                        <button type="button" class="kebab" @click.stop="openMenu(user, $event)" aria-label="Ações"><img src="images/icons/dots.svg" alt="" /></button>
                    </div>
                    <kv-tooltip v-if="!user.email && !user.phone" :text="t('users-tooltip_no_contact')"><div class="warn-contact">{{ t('users-no_contact') }}</div></kv-tooltip>
                    <div v-else class="card-lines">
                        <span v-if="user.email" class="contact-line contact-email">
                            <img src="images/icons/mail.svg" alt="" />
                            <span class="contact-text">{{ user.email }}</span>
                            <kv-tooltip v-if="!user.email_verified" :text="t('users-tooltip_email_pending')"><em class="pending"><img src="images/icons/alert-circle.svg" alt="" /></em></kv-tooltip>
                        </span>
                        <span v-if="user.phone" class="contact-line">
                            <img src="images/icons/phone.svg" alt="" />
                            <span class="contact-text">{{ user.phone }}</span>
                            <kv-tooltip v-if="!user.phone_verified" :text="t('users-tooltip_phone_pending')"><em class="pending"><img src="images/icons/alert-circle.svg" alt="" /></em></kv-tooltip>
                        </span>
                    </div>
                    <div class="card-foot">
                        <span class="contact-line" :class="{ 'is-twofa-on': user.twofa === 'on' }">
                            <img :src="user.twofa === 'on' ? 'images/icons/shield-tick-green.svg' : 'images/icons/shield-off.svg'" alt="" />
                            {{ user.twofa === 'on' ? t('users-twofa_on') : t('users-twofa_off') }}
                        </span>
                        <span class="when">{{ formatAccess(user.last_access) }}</span>
                    </div>
                </article>
            </div>

            <transition name="menu">
                <ul v-if="menuUser" key="action-menu" class="action-menu" :class="{ 'is-compact': isCompact }" :style="menuStyle" @click.stop>
                    <li><button type="button" @click="go('profile')"><img src="images/icons/user-edit.svg" alt="" />{{ t('users-menu-edit_profile') }}</button></li>
                    <li><button type="button" @click="go('permissions')"><img src="images/icons/edit-05.svg" alt="" />{{ t('users-menu-edit_permissions') }}</button></li>
                    <li><button type="button" @click="go('password')"><img src="images/icons/lock-01.svg" alt="" />{{ t('users-menu-set_password') }}</button></li>
                    <li v-if="menuUser.active === false && isGlobalAdmin">
                        <button type="button" @click="askActivate(menuUser)"><img src="images/icons/check-circle.svg" alt="" />Ativar usuário</button>
                    </li>
                    <li v-if="menuUser.active !== false">
                        <button type="button" class="danger" :disabled="!!deactivateBlock(menuUser)" :title="deactivateBlock(menuUser) ? blockedText(deactivateBlock(menuUser)) : ''" @click="askDeactivate">
                            <img src="images/icons/user-x-01.svg" alt="" />{{ t('users-menu-deactivate') }}
                        </button>
                    </li>
                </ul>
            </transition>

            <kv-pagination
                v-if="total > 0"
                :total="total"
                :current-page="page"
                :items-per-page="perPage"
                show-items-per-page
                @page-change="changePage"
                @items-per-page-change="changePerPage"
            />
        </div>

        <transition name="overlay">
        <div v-if="deactivateOpen" class="overlay" @click.self="closeDeactivate">
            <div class="dialog" role="dialog" @click.stop>
                <span class="warn-badge"><img src="images/icons/user-x-01.svg" alt="" /></span>
                <h2>{{ t('users-deactivate-title') }}</h2>
                <div v-if="deactivateTarget" class="who-chip">
                    <img v-if="deactivateTarget.logo" :src="deactivateTarget.logo" alt="" />
                    <span v-else>{{ initials(deactivateTarget.name) }}</span>
                    <div><strong>{{ deactivateTarget.name }}</strong><small>@{{ deactivateTarget.username }}</small></div>
                </div>
                <p>{{ t('users-deactivate-body') }}</p>
                <div v-if="deactivateTarget && deactivateTarget.in_kevi_chat" class="note">
                    <span class="note-icon"><img src="images/icons/logo-chat.svg" alt="" /></span>
                    <span>{{ t('users-deactivate-chat') }}</span>
                </div>
                <label class="secret">
                    <span class="sr-only">{{ t('users-password_modal-label') }}</span>
                    <input v-model="adminPassword" :type="showPassword ? 'text' : 'password'" :placeholder="t('users-password_modal-label')" :disabled="deactivateSubmitting" @keyup.enter="deactivate" />
                    <button type="button" :disabled="deactivateSubmitting" @click="showPassword = !showPassword" aria-label="Mostrar senha"><img src="images/icons/eye.svg" alt="" /></button>
                </label>
                <p v-if="deactivateError" class="error">{{ deactivateError }}</p>
                <div class="dialog-actions">
                    <button type="button" class="ghost" :disabled="deactivateSubmitting" @click="closeDeactivate">{{ t('users-deactivate-cancel') }}</button>
                    <button type="button" class="danger-btn" :disabled="deactivateSubmitting || !(adminPassword || '').trim()" @click="deactivate">
                        <span v-if="deactivateSubmitting" class="fa fa-spinner fa-spin" aria-hidden="true"></span>
                        {{ deactivateSubmitting ? t('users-password_modal-submitting') : t('users-deactivate-confirm') }}
                    </button>
                </div>
            </div>
        </div>
        </transition>

        <transition name="overlay">
        <div v-if="activateOpen" class="overlay" @click.self="closeActivate">
            <div class="dialog" role="dialog" @click.stop>
                <span class="warn-badge ok-badge"><img src="images/icons/check-circle.svg" alt="" /></span>
                <h2>Ativar usuário</h2>
                <div v-if="activateTarget" class="who-chip">
                    <img v-if="activateTarget.logo" :src="activateTarget.logo" alt="" />
                    <span v-else>{{ initials(activateTarget.name) }}</span>
                    <div><strong>{{ activateTarget.name }}</strong><small>@{{ activateTarget.username }}</small></div>
                </div>
                <p>O usuário volta a ficar ativo e recupera o acesso ao Kevi MKT. Confirme sua senha de administrador para continuar.</p>
                <label class="secret">
                    <span class="sr-only">{{ t('users-password_modal-label') }}</span>
                    <input v-model="adminPassword" :type="showPassword ? 'text' : 'password'" :placeholder="t('users-password_modal-label')" :disabled="activateSubmitting" @keyup.enter="activate" />
                    <button type="button" :disabled="activateSubmitting" @click="showPassword = !showPassword" aria-label="Mostrar senha"><img src="images/icons/eye.svg" alt="" /></button>
                </label>
                <p v-if="activateError" class="error">{{ activateError }}</p>
                <div class="dialog-actions">
                    <button type="button" class="ghost" :disabled="activateSubmitting" @click="closeActivate">Cancelar</button>
                    <button type="button" class="ok-btn" :disabled="activateSubmitting || !(adminPassword || '').trim()" @click="activate">
                        <span v-if="activateSubmitting" class="fa fa-spinner fa-spin" aria-hidden="true"></span>
                        {{ activateSubmitting ? t('users-password_modal-submitting') : 'Ativar' }}
                    </button>
                </div>
            </div>
        </div>
        </transition>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import KvPagination from "../components/ui/kv-pagination.vue";
import KvTooltip from "../components/ui/kv-tooltip.vue";

export default {
    name: "team-users",
    components: { KvPagination, KvTooltip },
    data() {
        return {
            items: [],
            total: 0,
            page: 1,
            perPage: 20,
            q: "",
            products: [],
            contacts: [],
            twofa: "",
            filtersOpen: true,
            openFilter: "",
            filterStyle: {},
            deactivateOpen: false,
            deactivateTarget: null,
            activateOpen: false,
            activateTarget: null,
            activateSubmitting: false,
            activateError: "",
            sort: "name",
            dir: "asc",
            sortColumns: [
                { key: "name", label: "users-columns-user" },
                { key: "email", label: "users-columns-contact" },
                { key: "phone", label: "users-columns-phone" },
                { key: "twofa", label: "users-columns-twofa" },
                { key: "last_access", label: "users-columns-last_access" },
            ],
            showPassword: false,
            loading: false,
            error: false,
            menuUser: null,
            menuStyle: {},
            isCompact: typeof window !== "undefined" ? window.innerWidth < 900 : false,
            resizeObserver: null,
            adminPassword: "",
            deactivateSubmitting: false,
            deactivateError: "",
            timer: null,
            contactFilters: ["email_verified", "email_pending", "no_email", "phone_verified", "phone_pending", "no_phone"],
            filterGroups: [
                {
                    id: "products",
                    label: "users-filters-products",
                    options: [
                        { value: "marketing", label: "users-filters-marketing" },
                        { value: "chat", label: "users-filters-chat" },
                    ],
                },
                {
                    id: "twofa",
                    label: "users-filters-twofa",
                    options: [
                        { value: "on", label: "users-twofa_on" },
                        { value: "off", label: "users-twofa_off" },
                    ],
                },
                {
                    id: "contacts",
                    label: "users-filters-contacts",
                    options: [
                        { value: "email_verified", label: "users-filters-email_verified" },
                        { value: "email_pending", label: "users-filters-email_pending" },
                        { value: "no_email", label: "users-filters-no_email" },
                        { value: "phone_verified", label: "users-filters-phone_verified" },
                        { value: "phone_pending", label: "users-filters-phone_pending" },
                        { value: "no_phone", label: "users-filters-no_phone" },
                    ],
                },
            ],
        };
    },
    created() {
        document.title = this.t("users-page_title");
        if (!this.canManage()) {
            this.$router.replace("/dashboard");
            return;
        }
        this.load();
    },
    mounted() {
        document.addEventListener("click", this.closePopovers);
        document.addEventListener("keydown", this.onKeydown);
        this.bindCompactLayout();
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closePopovers);
        document.removeEventListener("keydown", this.onKeydown);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        window.removeEventListener("resize", this.updateCompactLayout);
    },
    computed: {
        isGlobalAdmin() {
            const user = this.$store.state.user || {};
            return user.global_admin === true || user.global_admin === 1;
        },
        hasFilters() {
            return !!(this.q || this.products.length || this.contacts.length || this.twofa);
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        canManage() {
            const user = this.$store.state.user || {};
            const permission = user.permissions && user.permissions["account.users.manage"];
            return !!(user.global_admin || (permission && permission.enabled));
        },
        scheduleLoad() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.page = 1;
                this.load();
            }, 300);
        },
        isPicked(group, value) {
            if (group === "twofa") return this.twofa === value;
            if (group === "products") return this.products.includes(value);
            return this.contacts.includes(value);
        },
        pickFilter(group, value) {
            if (group === "twofa") {
                this.twofa = this.twofa === value ? "" : value;
                this.page = 1;
                this.load();
                return;
            }
            this.toggle(group, value);
        },
        toggle(list, value) {
            const current = this[list];
            this[list] = current.includes(value) ? current.filter((item) => item !== value) : current.concat(value);
            this.page = 1;
            this.load();
        },
        sortBy(key) {
            if (this.sort === key) {
                this.dir = this.dir === "asc" ? "desc" : "asc";
            } else {
                this.sort = key;
                this.dir = key === "last_access" ? "desc" : "asc";
            }
            this.page = 1;
            this.load();
        },
        ariaSort(key) {
            if (this.sort !== key) return "none";
            return this.dir === "asc" ? "ascending" : "descending";
        },
        deactivateBlock(user) {
            if (!user) return null;
            if (user.blocked_actions && user.blocked_actions.deactivate) return user.blocked_actions.deactivate;
            if (user.is_self) return "self";
            if (user.global_admin) return "protected";
            return null;
        },
        blockedText(reason) {
            const map = {
                self: "users-deactivate-self",
                protected: "users-deactivate-protected",
                last_admin: "users-deactivate-last_admin",
            };
            return this.t(map[reason] || "users-deactivate-protected");
        },
        changePage(next) {
            this.page = next;
            this.load();
        },
        changePerPage(next) {
            this.perPage = next;
            this.page = 1;
            this.load();
        },
        clearFilters() {
            this.q = "";
            this.products = [];
            this.contacts = [];
            this.twofa = "";
            this.page = 1;
            this.load();
        },
        load() {
            this.loading = true;
            this.error = false;
            this.menuUser = null;
            const options = keviApi.getOptions("place/team-users", "GET", {
                q: this.q,
                page: this.page,
                per_page: this.perPage,
                products: this.products.join(","),
                contacts: this.contacts.join(","),
                twofa: this.twofa,
                sort: this.sort,
                dir: this.dir,
            });
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    this.items = response.body.items || [];
                    this.total = response.body.total || 0;
                },
                () => {
                    this.loading = false;
                    this.error = true;
                }
            );
        },
        initials(name) {
            return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
        },
        formatAccess(value) {
            if (!value) return this.t("users-never");
            const date = new Date(String(value).replace(" ", "T"));
            if (Number.isNaN(date.getTime())) return value;
            const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            const start = new Date();
            start.setHours(0, 0, 0, 0);
            const day = new Date(date);
            day.setHours(0, 0, 0, 0);
            const diff = (start - day) / 86400000;
            if (diff === 0) return this.t("users-today", { 0: time });
            if (diff === 1) return this.t("users-yesterday", { 0: time });
            return date.toLocaleDateString() + ", " + time;
        },
        sameUser(left, right) {
            return String(left.id) === String(right.id);
        },
        bindCompactLayout() {
            this.updateCompactLayout();
            window.addEventListener("resize", this.updateCompactLayout);
            const box = this.$el && this.$el.querySelector(".box");
            if (box && typeof ResizeObserver !== "undefined") {
                this.resizeObserver = new ResizeObserver(() => this.updateCompactLayout());
                this.resizeObserver.observe(box);
            }
        },
        updateCompactLayout() {
            const box = this.$el && this.$el.querySelector(".box");
            const width = box ? box.getBoundingClientRect().width : window.innerWidth;
            // Table needs ~760px; sidebar layouts leave ~600px content — switch to cards earlier.
            const next = width < 760 || window.innerWidth < 900;
            if (next !== this.isCompact) {
                this.isCompact = next;
                this.menuUser = null;
            }
        },
        toggleFilter(id, event) {
            this.menuUser = null;
            if (this.openFilter === id) {
                this.openFilter = "";
                return;
            }
            this.openFilter = id;
            const rect = event.currentTarget.getBoundingClientRect();
            const width = 220;
            const height = 280;
            let left = rect.left;
            if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
            let top = rect.bottom + 6;
            if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 6);
            this.filterStyle = {
                position: "fixed",
                top: top + "px",
                left: left + "px",
                zIndex: 50,
            };
        },
        closePopovers() {
            this.openFilter = "";
            this.menuUser = null;
        },
        onKeydown(event) {
            if (event.key === "Escape") this.closePopovers();
        },
        openMenu(user, event) {
            if (this.menuUser && this.sameUser(this.menuUser, user)) {
                this.menuUser = null;
                return;
            }
            this.openFilter = "";
            const rect = event.currentTarget.getBoundingClientRect();
            const width = 220;
            const height = 188;
            let left = rect.right - width;
            if (left < 8) left = 8;
            if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
            let top = rect.bottom + 6;
            if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 6);
            this.menuStyle = {
                position: "fixed",
                top: top + "px",
                left: left + "px",
                right: "auto",
                zIndex: 60,
            };
            if (this.menuUser) {
                this.menuUser = null;
                this.$nextTick(() => { this.menuUser = user; });
            } else {
                this.menuUser = user;
            }
        },
        go(section) {
            const id = this.menuUser && this.menuUser.id;
            if (!id) return;
            this.$router.push("/settings/users/" + encodeURIComponent(String(id)) + "/" + section);
        },
        askDeactivate() {
            if (!this.menuUser || this.deactivateBlock(this.menuUser)) return;
            // Guarda o alvo à parte: cliques no modal fecham o menu e zeram menuUser.
            this.deactivateTarget = this.menuUser;
            this.menuUser = null;
            this.deactivateError = "";
            this.adminPassword = "";
            this.deactivateSubmitting = false;
            this.deactivateOpen = true;
        },
        askActivate(user) {
            if (!user || !this.isGlobalAdmin) return;
            // Guarda o alvo à parte: cliques no modal fecham o menu e zeram menuUser.
            this.activateTarget = user;
            this.menuUser = null;
            this.activateError = "";
            this.adminPassword = "";
            this.showPassword = false;
            this.activateSubmitting = false;
            this.activateOpen = true;
        },
        closeActivate() {
            if (this.activateSubmitting) return;
            this.activateOpen = false;
            this.activateTarget = null;
            this.adminPassword = "";
        },
        activate() {
            if (this.activateSubmitting || !this.activateTarget) return;
            if (!(this.adminPassword || "").trim()) return;
            this.activateSubmitting = true;
            this.activateError = "";
            const options = keviApi.getOptions("place/team-users/" + this.activateTarget.id + "/activate", "POST", {
                admin_password: this.adminPassword,
            });
            this.$http(options).then(
                () => {
                    this.activateSubmitting = false;
                    this.activateOpen = false;
                    this.activateTarget = null;
                    this.adminPassword = "";
                    this.$store.dispatch("notification/success", "Usuário ativado.");
                    this.load();
                },
                (response) => {
                    this.activateSubmitting = false;
                    const code = response.body && response.body.message;
                    const map = {
                        wrong_password: this.t("users-password_modal-wrong"),
                        too_many_attempts: this.t("users-password_modal-too_many"),
                        forbidden_target: this.t("users-deactivate-protected"),
                        place_without_product: "Este estabelecimento não tem o Kevi MKT contratado.",
                    };
                    this.activateError = map[code]
                        || (response.status === 429 ? this.t("users-password_modal-too_many") : "Não foi possível ativar o usuário.");
                }
            );
        },
        closeDeactivate() {
            if (this.deactivateSubmitting) return;
            this.deactivateOpen = false;
            this.deactivateTarget = null;
        },
        deactivate() {
            if (this.deactivateSubmitting || !this.deactivateTarget) return;
            if (!(this.adminPassword || "").trim()) return;
            this.deactivateSubmitting = true;
            this.deactivateError = "";
            const options = keviApi.getOptions("place/team-users/" + this.deactivateTarget.id + "/deactivate", "POST", {
                admin_password: this.adminPassword,
            });
            this.$http(options).then(
                () => {
                    this.deactivateSubmitting = false;
                    this.deactivateOpen = false;
                    this.deactivateTarget = null;
                    this.$store.dispatch("notification/success", this.t("users-deactivate-done"));
                    this.load();
                },
                (response) => {
                    this.deactivateSubmitting = false;
                    const code = response.body && response.body.message;
                    const map = {
                        wrong_password: "users-password_modal-wrong",
                        too_many_attempts: "users-password_modal-too_many",
                        self: "users-deactivate-self",
                        protected: "users-deactivate-protected",
                        forbidden_target: "users-deactivate-protected",
                        last_admin: "users-deactivate-last_admin",
                    };
                    const fallback = response.status === 429 ? "users-password_modal-too_many" : "users-deactivate-failed";
                    this.deactivateError = this.t(map[code] || fallback);
                }
            );
        },
    },
};
</script>

<style scoped>
.users-page { background: #f7f7f8; min-height: calc(100vh - 72px); padding: 16px; font-family: "Inter", sans-serif; }
.users-head { display: flex; align-items: center; gap: 16px; background: #fff; border: 1px solid #e5e6eb; border-radius: 12px; padding: 16px; margin-bottom: 8px; }
.head-icon { width: 40px; height: 40px; border-radius: 100px; background: #edfcf3; display: flex; align-items: center; justify-content: center; flex: none; }
.head-icon img { width: 24px; height: 24px; display: block; }
.users-head h1 { margin: 0; font-family: "Sora", sans-serif; font-size: 24px; font-weight: 400; line-height: 1.3; color: #626473; }
.users-head p { margin: 8px 0 0; font-size: 14px; font-weight: 400; line-height: 1.2; color: #9598a6; }
.box { background: #fff; border: 1px solid #e5e6eb; border-radius: 12px; padding: 0; overflow: visible; }
.person { display: flex; justify-content: flex-start; gap: 10px; align-items: center; min-width: 0; }
.muted { color: #626473; margin: 0; font-size: 12px; }
.users-toolbar { display: flex; flex-direction: column; gap: 16px; padding: 16px; border-bottom: 1px solid #e5e6eb; }
.toolbar-main { display: flex; align-items: center; gap: 8px; }
.search { display: flex; align-items: center; gap: 8px; width: 197px; height: 36px; box-sizing: border-box; padding: 8px; border: 1px solid #e5e6eb; border-radius: 8px; background: #fafafc; margin: 0; }
.search img { width: 20px; height: 20px; flex: none; }
.search input { border: 0; background: transparent; width: 100%; padding: 0; margin: 0; height: 20px; font-family: "Inter", sans-serif; font-size: 12px; color: #1f2337; box-shadow: none; outline: none; }
.search input::placeholder { color: #bbbdc7; }
.filter-btn { width: 36px; height: 36px; border: 0; border-radius: 8px; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.filter-btn.on, .filter-btn:hover { background: #f3f3f6; }
.filter-btn img { width: 20px; height: 20px; }
.add { margin-left: auto; min-height: 30px; padding: 6px; border-radius: 8px; background: #2aaa4c; color: #f1fcf3; font-size: 14px; font-weight: 400; line-height: 1.2; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
.add img { width: 16px; height: 16px; display: block; }
.users-filters { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 0 16px 16px; color: #626473; font-size: 13px; }
.users-page .form-control { box-sizing: border-box; height: 44px; margin: 8px 0 0; padding: 0 12px; border-radius: 8px; border: 1px solid #e5e6eb; background: #f9f9fb; box-shadow: none; font-size: 12px; color: #1f2337; width: 100%; }
.filter-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-start; overflow: hidden; }
.pill { position: relative; }
.pill > button { display: inline-flex; align-items: center; gap: 4px; height: 24px; padding: 4px; border: 0; border-radius: 1000px; background: #edfcf3; color: #2aaa4c; font-family: "Inter", sans-serif; font-size: 12px; font-weight: 400; line-height: 1.3; cursor: pointer; }
.pill > button span { padding: 0 2px; }
.pill > button img { width: 12px; height: 12px; display: block; transition: transform .16s ease; }
.pill.open > button img { transform: rotate(180deg); }
.filter-menu { position: absolute; top: calc(100% + 6px); left: 0; z-index: 4; min-width: 220px; margin: 0; padding: 6px; list-style: none; background: #fff; border: 1px solid #e5e6eb; border-radius: 12px; box-shadow: 0 12px 32px rgba(16, 24, 40, .12); }
.filter-menu button { display: flex; align-items: center; gap: 8px; width: 100%; border: 0; background: transparent; border-radius: 8px; padding: 8px; text-align: left; font-family: "Inter", sans-serif; font-size: 14px; color: #383c50; cursor: pointer; }
.filter-menu button:hover { background: #f7f7f8; }
.tick { width: 16px; height: 16px; border: 1px solid #d2d3db; border-radius: 4px; box-sizing: border-box; flex: none; background: #fff; }
.tick.on { background: #2aaa4c; border-color: #2aaa4c; box-shadow: inset 0 0 0 2px #fff; }
.users-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.users-table .col-user { width: 30%; }
.users-table .col-email { width: 28%; }
.users-table .col-phone { width: 16%; }
.users-table .col-twofa { width: 12%; }
.users-table .col-access { width: 12%; }
.users-table .col-menu { width: 44px; }
.users-table th { color: #626473; font-weight: 500; font-size: 12px; text-align: left; padding: 10px 12px; border-bottom: 1px solid #e5e6eb; white-space: nowrap; }
.users-table td { border-bottom: 1px solid #e5e6eb; padding: 10px 12px; vertical-align: middle; font-family: "Inter", sans-serif; overflow: hidden; }
.users-table td:nth-child(2) { overflow: visible; }
.users-table .label { white-space: nowrap; }
.who { display: flex; flex-direction: column; gap: 4px; flex: 1 1 auto; min-width: 96px; }
.who strong { font-size: 14px; font-weight: 500; color: #1f2337; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.who > span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.who span, .when, .contact-line { font-size: 12px; font-weight: 500; color: #626473; }
.who > .kv-tooltip-container { display: block; min-width: 0; max-width: 100%; }
.who > .kv-tooltip-container > strong, .who > .kv-tooltip-container > span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sort-btn { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: none; font: inherit; color: inherit; cursor: pointer; }
.sort-btn img { width: 12px; height: 12px; transition: transform .16s ease, opacity .16s ease; }
.sort-btn img.up { transform: rotate(180deg); }
.sort-btn img.idle { opacity: 0; }
.sort-btn:hover img.idle { opacity: .5; }
.sort-btn.on { color: #1f2337; }
.skeleton { display: flex; flex-direction: column; }
.skeleton-row { display: grid; grid-template-columns: 32px 1.2fr 1.4fr 1fr .8fr; gap: 16px; align-items: center; padding: 14px 16px; border-bottom: 1px solid #e5e6eb; }
.sk { display: block; height: 12px; border-radius: 6px; background: linear-gradient(90deg, #f3f3f6 25%, #e9e9ee 37%, #f3f3f6 63%); background-size: 400% 100%; animation: shimmer 1.4s ease infinite; }
.sk-avatar { width: 32px; height: 32px; border-radius: 50%; }
.sk-short { width: 60%; }
@keyframes shimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
.avatar { width: 32px; height: 32px; border-radius: 50%; background: #f3f3f6; display: inline-flex; align-items: center; justify-content: center; object-fit: cover; font-size: 12px; font-weight: 500; color: #626473; flex: 0 0 32px; }
.products { display: inline-flex; align-items: center; gap: 4px; flex: 0 0 auto; margin-left: auto; }
.tag { display: inline-flex; align-items: center; flex: 0 0 auto; margin-left: 8px; padding: 2px 8px; border-radius: 999px; background: #f3f3f6; color: #656565; font-size: 11px; font-weight: 500; white-space: nowrap; }
.tag-off { background: #fdecec; color: #b42318; }
.tag-internal { background: #eef0ff; color: #3b44c4; }
.tag-action { flex: 0 0 auto; margin-left: 6px; padding: 2px 10px; border: 1px solid #12b76a; border-radius: 999px; background: #fff; color: #027a48; font-size: 11px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.tag-action:hover { background: #ecfdf3; }
.product { width: 24px; height: 24px; border-radius: 50%; background: #f1fcf3; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 24px; }
.product img { width: 12px; height: 14px; }
.menu-cell { position: relative; width: 44px; }
.kebab { width: 28px; height: 28px; border: 0; background: transparent; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.kebab:hover { background: #f3f3f6; }
.kebab img { width: 16px; height: 16px; }
.action-menu {
    position: fixed;
    z-index: 60;
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    list-style: none;
    margin: 0;
    padding: 6px;
    min-width: 200px;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.12);
    transform-origin: top right;
    will-change: opacity, transform;
}
.action-menu button {
    background: none;
    border: 0;
    width: 100%;
    text-align: left;
    padding: 8px 10px;
    border-radius: 8px;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    color: #384250;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}
.action-menu button:hover { background: #f4f5f7; }
.action-menu img { width: 20px; height: 20px; flex: none; display: block; }
.action-menu .danger { color: #f04438; background: #fef3f2; }
.action-menu .danger:hover { background: #fee4e2; }
.action-menu .danger:disabled { opacity: .5; cursor: not-allowed; }
.filters-enter-active, .filters-leave-active {
    transition: opacity .2s ease, transform .22s cubic-bezier(.16, 1, .3, 1), max-height .24s ease;
    max-height: 80px;
}
.filters-enter, .filters-leave-to { opacity: 0; transform: translateY(-8px); max-height: 0; }
.menu-enter-active, .menu-leave-active, .pop-enter-active, .pop-leave-active {
    transition: opacity .16s ease, transform .18s cubic-bezier(.16, 1, .3, 1);
}
.menu-enter, .menu-leave-to, .pop-enter, .pop-leave-to { opacity: 0; transform: translateY(-4px) scale(.96); }
.user-cards { display: flex; flex-direction: column; gap: 12px; padding: 12px 16px 8px; }
.user-card { position: relative; border: 1px solid #e5e6eb; border-radius: 16px; padding: 12px; display: flex; flex-direction: column; gap: 8px; background: #fff; }
.card-top, .card-foot { display: flex; align-items: center; gap: 8px; }
.card-top .who { flex: 1; min-width: 0; }
.card-lines { display: flex; flex-direction: column; gap: 6px; }
.card-foot { justify-content: space-between; }
.users-page { animation: rise .28s cubic-bezier(.16, 1, .3, 1); }
.kv-pagination, .kv-table-footer { padding: 0 16px 16px; }
@media (max-width: 900px) {
    .users-page { padding: 0 0 24px; }
    .users-head, .box { border-radius: 0; }
    .search { flex: 1; width: auto; min-width: 0; }
    .filter-row { overflow-x: auto; flex-wrap: nowrap; }
    .pill { flex: none; }
}
@keyframes rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.empty { min-height: 280px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 48px 24px; text-align: center; }
.empty h2 { margin: 8px 0 0; font-family: "Sora", sans-serif; font-size: 20px; font-weight: 400; line-height: 1.3; color: #1f2337; }
.empty p { margin: 0; max-width: 320px; font-size: 14px; line-height: 1.3; color: #626473; }
.empty-icon { width: 40px; height: 40px; border-radius: 100px; background: #edfcf3; display: flex; align-items: center; justify-content: center; }
.empty-icon.alert { background: #fffbed; }
.empty-icon img { width: 24px; height: 24px; }
.empty-actions { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.ghost, .danger-btn { min-height: 43px; padding: 12px 16px; border-radius: 8px; font-family: "Inter", sans-serif; font-size: 16px; font-weight: 400; line-height: 1.2; cursor: pointer; }
.ghost { border: 1px solid #e5e6eb; background: #fafafc; color: #383c50; }
.danger-btn { border: 0; background: #fef3f2; color: #f04438; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
.danger-btn:disabled, .ghost:disabled { opacity: .65; cursor: default; }
.ok-btn { min-height: 43px; padding: 12px 16px; border: 0; border-radius: 8px; background: #ecfdf3; color: #027a48; font-family: "Inter", sans-serif; font-size: 16px; line-height: 1.2; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
.ok-btn:disabled { opacity: .65; cursor: default; }
.ok-badge { background: #ecfdf3; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }
.spinner { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #d9e5ff; border-top-color: #2f6bff; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.pager { display: flex; gap: 8px; align-items: center; justify-content: flex-end; padding: 12px 16px; }
.pager button { width: 32px; height: 32px; border: 1px solid #e5e6eb; background: #fafafc; border-radius: 8px; color: #383c50; cursor: pointer; }
.pager button:disabled { color: #d2d3db; cursor: default; }
.pager span { font-size: 12px; color: #626473; }
.overlay { position: fixed; inset: 0; background: rgba(31, 35, 55, .35); display: flex; align-items: center; justify-content: center; z-index: 40; padding: 16px; }
.dialog { width: min(343px, 100%); background: #fff; border-radius: 16px; padding: 16px; display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center; }
.dialog h2 { margin: 0; font-family: "Sora", sans-serif; font-size: 20px; font-weight: 400; line-height: 1.3; color: #1f2337; }
.dialog p, .note { margin: 0; font-size: 14px; line-height: 1.3; color: #626473; }
.note { display: flex; align-items: center; gap: 8px; background: #f1fcf3; color: #209941; border-radius: 8px; padding: 10px 12px; width: 100%; box-sizing: border-box; text-align: left; }
.note-icon { width: 24px; height: 24px; border-radius: 50%; background: #defae5; display: inline-flex; align-items: center; justify-content: center; flex: none; }
.note-icon img { width: 12px; height: 14px; }
.warn-badge { width: 40px; height: 40px; border-radius: 50%; background: #fef9f9; display: flex; align-items: center; justify-content: center; }
.warn-badge img { width: 24px; height: 24px; }
.who-chip { display: flex; align-items: center; gap: 8px; background: #fafafc; border: 1px solid #e5e6eb; border-radius: 12px; padding: 8px 12px; text-align: left; }
.who-chip img, .who-chip > span { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background: #f3f3f6; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #626473; }
.who-chip strong { display: block; font-size: 14px; color: #1f2337; }
.who-chip small { color: #626473; font-size: 12px; }
.secret { position: relative; width: 100%; margin: 0; }
.secret input { box-sizing: border-box; width: 100%; height: 44px; padding: 0 40px 0 12px; border: 1px solid #bbbdc7; border-radius: 8px; background: #f9f9fb; font-size: 12px; color: #1f2337; }
.secret button { position: absolute; right: 12px; top: 12px; border: 0; background: transparent; padding: 0; cursor: pointer; }
.secret img { width: 20px; height: 20px; display: block; }
.dialog .error { margin: 0; color: #d92d20; font-size: 13px; }
.dialog-actions { display: flex; gap: 8px; width: 100%; }
.dialog-actions .ghost, .dialog-actions .danger-btn, .dialog-actions .ok-btn { flex: 1; }
.overlay-enter-active, .overlay-leave-active { transition: opacity .2s ease; }
.overlay-enter, .overlay-leave-to { opacity: 0; }
.dialog { transition: transform .24s cubic-bezier(.16, 1, .3, 1), opacity .2s ease; }
.overlay-enter .dialog, .overlay-leave-to .dialog { opacity: 0; transform: translateY(12px) scale(.98); }
.warn-contact { color: #e07a12; font-weight: 600; font-size: 12px; }
.contact-line { display: inline-flex; align-items: center; gap: 6px; min-width: 0; max-width: 100%; }
.contact-line.is-twofa-on { color: #0b9055; }
.contact-line.label { flex-wrap: nowrap; max-width: none; white-space: nowrap; }
.contact-line > img { width: 16px; height: 16px; flex: none; }
.contact-text { min-width: 0; overflow-wrap: anywhere; word-break: break-word; line-height: 1.35; }
.contact-email { align-items: flex-start; white-space: normal; }
.pending { width: 16px; height: 16px; border-radius: 50%; background: #fffbed; display: inline-flex; align-items: center; justify-content: center; }
.pending img { width: 12px; height: 12px; }
</style>
